import {
  Circle,
  Send,
  Eye,
  Pencil,
  CheckCircle,
  AlertCircle,
  Mail,
  RefreshCw,
  BadgeCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SupplierAggregateStatus, SupplierSubmissionStatus } from '@/lib/api'

type Size = 'sm' | 'md'

type StatusEntry = {
  dot: string
  label: string
  icon: React.ReactNode
  container: string
}

const STATUS_CONFIG_V2: Record<SupplierSubmissionStatus, StatusEntry> = {
  NOT_SENT: {
    dot: 'bg-gray-400',
    label: 'Non envoyé',
    icon: <Circle className="h-3 w-3" />,
    container: 'bg-gray-100 text-gray-700',
  },
  SENT: {
    dot: 'bg-blue-400',
    label: 'Envoyé',
    icon: <Send className="h-3 w-3" />,
    container: 'bg-blue-50 text-blue-700',
  },
  OPENED: {
    dot: 'bg-blue-600',
    label: 'Ouvert',
    icon: <Eye className="h-3 w-3" />,
    container: 'bg-blue-100 text-blue-800',
  },
  IN_PROGRESS: {
    dot: 'bg-amber-600',
    label: 'En cours',
    icon: <Pencil className="h-3 w-3" />,
    container: 'bg-amber-100 text-amber-800',
  },
  SUBMITTED: {
    dot: 'bg-violet-600',
    label: 'Soumis',
    icon: <CheckCircle className="h-3 w-3" />,
    container: 'bg-violet-100 text-violet-800',
  },
  NEEDS_COMPLEMENT: {
    dot: 'bg-pink-600',
    label: 'Complément demandé',
    icon: <AlertCircle className="h-3 w-3" />,
    container: 'bg-pink-100 text-pink-800',
  },
  COMPLEMENT_SENT: {
    dot: 'bg-pink-400',
    label: 'Complément envoyé',
    icon: <Mail className="h-3 w-3" />,
    container: 'bg-pink-50 text-pink-700',
  },
  RESUBMITTED: {
    dot: 'bg-violet-700',
    label: 'Re-soumis',
    icon: <RefreshCw className="h-3 w-3" />,
    container: 'bg-violet-200 text-violet-900',
  },
  VALIDATED: {
    dot: 'bg-green-600',
    label: 'Validé',
    icon: <BadgeCheck className="h-3 w-3" />,
    container: 'bg-green-100 text-green-800',
  },
}

const NO_CAMPAIGN_CONFIG: StatusEntry = {
  dot: 'bg-gray-300',
  label: 'Jamais invité',
  icon: <Circle className="h-3 w-3" />,
  container: 'bg-gray-50 text-gray-500',
}

export function SupplierStatusBadgeV2({
  status,
  size = 'sm',
}: {
  status: SupplierAggregateStatus
  size?: Size
}) {
  const config = status === 'NO_CAMPAIGN' ? NO_CAMPAIGN_CONFIG : STATUS_CONFIG_V2[status]
  const sizeClasses =
    size === 'md' ? 'px-3 py-1 text-sm' : 'px-2.5 py-0.5 text-xs'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        sizeClasses,
        config.container
      )}
      role="status"
      aria-label={`Statut: ${config.label}`}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} aria-hidden="true" />
      {config.label}
      {config.icon}
    </span>
  )
}
